import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import CommercialLayout from "../../components/layouts/CommercialLayout";
import api from "../../services/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const Historique = () => {
  const { utilisateur } = useAuth();
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/commercials/${utilisateur.id}/historique-journalier`);
        setHistorique(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (utilisateur?.id) fetch();
  }, [utilisateur]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  };

  // Données pour le graphe (ordre chronologique)
  const dataGraphe = [...historique].reverse().map((h) => ({
    date: formatDate(h.date),
    coupons: Number(h.nb_coupons_vendus),
    mobilise: Number(h.montant_mobilise),
    encaisse: Number(h.montant_encaisse),
  }));

  if (loading) return (
    <CommercialLayout>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <div style={{ width: "36px", height: "36px", border: "3px solid #E6F1FB", borderTop: "3px solid #185FA5", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    </CommercialLayout>
  );

  return (
    <CommercialLayout>
      {/* Header */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ fontSize: "18px", fontWeight: "500", color: "#111827", margin: 0 }}>Historique journalier</h1>
        <p style={{ fontSize: "13px", color: "#6b7280", margin: "4px 0 0" }}>{historique.length} jour(s) enregistré(s)</p>
      </div>

      {historique.length === 0 ? (
        <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "3rem", textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📋</div>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>Aucun historique disponible pour le moment</p>
        </div>
      ) : (
        <>
          {/* Graphe */}
          <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.25rem", marginBottom: "1.25rem" }}>
            <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827", marginBottom: "1.25rem" }}>
              Nombre de ventes
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dataGraphe}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} />
                <Tooltip
                  contentStyle={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "8px", fontSize: "12px" }}
                />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Line
                  type="monotone" dataKey="coupons" name="Coupons vendus"
                  stroke="#185FA5" strokeWidth={2} dot={{ r: 4, fill: "#185FA5" }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone" dataKey="mobilise" name="Montant mobilisé (F)"
                  stroke="#ea580c" strokeWidth={2} dot={{ r: 4, fill: "#ea580c" }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Tableau */}
          <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ padding: "1rem 1.25rem", borderBottom: "0.5px solid #e5e7eb" }}>
              <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827" }}>Détail par jour</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f9fafb" }}>
                    {["Date", "Caisse début", "Clients", "Coupons", "Mobilisé", "Encaissé", "Solde rendu", "Pénalités", "Reçu", "Envoyé", "À temps", "Retard <24h", "Retard >24h", "Tardifs"].map((h) => (
                      <th key={h} style={{ padding: "0.75rem 1rem", textAlign: "left", fontSize: "11px", color: "#6b7280", fontWeight: "500", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historique.map((row, i) => (
                    <tr key={i} style={{ borderTop: "0.5px solid #f3f4f6" }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      <td style={{ padding: "0.75rem 1rem", fontWeight: "500", color: "#111827", whiteSpace: "nowrap" }}>
                        {new Date(row.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#374151", whiteSpace: "nowrap" }}>
                        {Number(row.caisse_debut).toLocaleString("fr-FR")} F
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#374151" }}>
                        {row.nb_clients}
                      </td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        <span style={{ backgroundColor: "#E6F1FB", color: "#185FA5", padding: "2px 8px", borderRadius: "6px", fontWeight: "500" }}>
                          {row.nb_coupons_vendus}
                        </span>
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#ea580c", whiteSpace: "nowrap" }}>
                        {Number(row.montant_mobilise).toLocaleString("fr-FR")} F
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#111827", whiteSpace: "nowrap" }}>
                        {Number(row.montant_encaisse).toLocaleString("fr-FR")} F
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#16a34a", whiteSpace: "nowrap" }}>
                        {Number(row.solde_rendu).toLocaleString("fr-FR")} F
                      </td>
                      <td style={{ padding: "0.75rem 1rem", whiteSpace: "nowrap" }}>
                        {Number(row.penalites_recuperees) > 0 ? (
                          <span style={{ color: "#dc2626" }}>{Number(row.penalites_recuperees).toLocaleString("fr-FR")} F</span>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#16a34a", whiteSpace: "nowrap" }}>{Number(row.transfert_recu) > 0 ? `+${Number(row.transfert_recu).toLocaleString("fr-FR")} F` : "—"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: "#ea580c", whiteSpace: "nowrap" }}>{Number(row.transfert_envoye) > 0 ? `-${Number(row.transfert_envoye).toLocaleString("fr-FR")} F` : "—"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: "#16a34a", whiteSpace: "nowrap" }}>{Number(row.montant_remb_a_temps) > 0 ? `${Number(row.montant_remb_a_temps).toLocaleString("fr-FR")} F` : "—"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: "#ca8a04", whiteSpace: "nowrap" }}>{Number(row.montant_remb_retard_24h) > 0 ? `${Number(row.montant_remb_retard_24h).toLocaleString("fr-FR")} F` : "—"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: "#ea580c", whiteSpace: "nowrap" }}>{Number(row.montant_remb_retard_sup_24h) > 0 ? `${Number(row.montant_remb_retard_sup_24h).toLocaleString("fr-FR")} F` : "—"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: "#dc2626", whiteSpace: "nowrap" }}>{Number(row.montant_remb_tardifs) > 0 ? `${Number(row.montant_remb_tardifs).toLocaleString("fr-FR")} F` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </CommercialLayout>
  );
};

export default Historique;